import { toast } from 'sonner';
import { i18n } from '@/lib/i18n';
import { ApiError } from '@/lib/api-client';
import { recordFeedbackBreadcrumb } from '@/lib/feedback-diagnostics';

/** Nutzt die Zwischenablage, fällt aber auf eine Auswahl-Hilfe zurück. */
async function copy(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function describe(error: unknown): { title: string; messages: string[] } {
  const t = i18n.t.bind(i18n);

  if (!(error instanceof ApiError)) {
    return {
      title: t('errors.unexpectedTitle'),
      messages: [error instanceof Error ? error.message : t('errors.unexpectedMessage')],
    };
  }

  return {
    title: error.title ?? t('errors.requestFailed'),
    messages: [
      error.message,
      ...(error.hint === undefined ? [] : [error.hint]),
      ...(error.details ?? []).map((detail) => detail.message),
    ],
  };
}

/** Formatiert sämtliche API-Details, ohne doppelte Meldungen im Toast auszugeben. */
export function apiErrorDescription(error: unknown): string {
  return [...new Set(describe(error).messages.filter((message) => message.trim().length > 0))].join(
    '\n',
  );
}

/**
 * Meldet einen fehlgeschlagenen Aufruf als Toast. Protokollierte Fehler zeigen
 * ihre Referenz-ID an und lassen sie für die Supportanfrage kopieren.
 */
export function toastApiError(error: unknown, titleOverride?: string): void {
  const described = describe(error);
  const trackingId = error instanceof ApiError ? error.trackingId : undefined;
  const t = i18n.t.bind(i18n);

  const description = apiErrorDescription(error);
  recordFeedbackBreadcrumb({
    type: 'TOAST_ERROR',
    actionId: 'toast.error.shown',
    actionLabelKey: 'diagnostics.actions.toastError',
    entityType: null,
    entityId: null,
    result: 'FAILED',
    errorTrackingId: trackingId ?? null,
  });

  if (trackingId === undefined) {
    toast.error(titleOverride ?? described.title, { description });
    return;
  }

  toast.error(`${titleOverride ?? described.title} [${trackingId}]`, {
    description: `${description} ${t('errors.copyHint')}`,
    duration: 10_000,
    action: {
      label: t('errors.copyId'),
      onClick: () => {
        void copy(trackingId).then((ok) => {
          if (ok) {
            toast.success(t('errors.copied', { id: trackingId }));
          }
        });
      },
    },
  });
}
